<template>
	<div
		v-if="ready"
		class="layout"
		ref="layout"
	>
		<grid-layout
			:layout.sync="slotsItems"
			:col-num="options.columns"
			:row-height="options.rowHeight"
			:is-draggable="preview || isTeamViewer ? false : options.draggable"
			:margin="options.margin"
			:vertical-compact="options.verticalCompact"
			:maxRows="options.maxRows"
			:use-css-transforms="options.cssTransforms"
			:preserve-aspect-ratio="options.preserveAspectRatio"
			:preventCollision="options.preventCollision"
		>
			<grid-item
				v-for="item in slotsItems"
				:key="item.i"
				:x="item.x"
				:y="item.y"
				:w="item.w"
				:h="item.h"
				:i="item.i"
				:static="item.static"
				:is-resizable="!(preview || isTeamViewer)"
			>
				<div class="layout-slot">

					<!-- lock/unlock slot -->
					<el-tooltip
						v-if="!(preview || isTeamViewer)"
						effect="dark"
						:content="item.static ? translations.labels.locked : translations.labels.unlocked"
						placement="top"
					>
						<el-button
							@click="updateStatic(item.i)"
							class="layout-slot-button static"
							:type="item.static ? 'info' : 'success'"
							:icon="item.static ? 'el-icon-lock' : 'el-icon-unlock'"
							size="mini"
							circle
							plain
						/>
					</el-tooltip>
					<!-- /lock/unlock slot -->

					<!-- delete slot -->
					<el-popconfirm
						v-if="!(preview || isTeamViewer) && !item.static"
						@confirm="deleteSlot(item.i)"
						:title="translations.messages.warningRemoveSlot"
						:cancel-button-text="translations.labels.noCancel"
						confirm-button-text="OK"
						hide-icon
					>
						<el-button
							class="layout-slot-button delete"
							type="danger"
							icon="el-icon-delete"
							size="mini"
							slot="reference"
							circle
							plain
						/>
					</el-popconfirm>
					<!-- /delete slot -->

					<!-- manage slot -->
					<el-tooltip
						v-if="!(preview || isTeamViewer)"
						effect="dark"
						:content="translations.labels.manageSlot"
						placement="top"
					>
						<el-button
							@click="openWidgetManager(item.i)"
							class="layout-slot-button manage"
							type="primary"
							icon="el-icon-s-tools"
							size="mini"
							circle
							plain
						/>
					</el-tooltip>
					<!-- /manage slot -->

					<!-- widgets -->
					<div class="widgets">

						<!-- preview -->
						<div
							v-if="preview"
							class="widgets-area"
						>

							<Widget
								v-if="item.widgets.length === 1"
								:widget="item.widgets[0]"
							/>

							<slider
								v-if="item.widgets.length > 1"
								animation="fade"
								:autoplay="true"
								:interval="10000"
								:indicators="false"
								:control-btn="false"
								:touch="false"
								:speed="500"
							>
								<slider-item
									v-for="slide in item.widgets"
									:key="slide.unique"
								>
									<Widget :widget="slide" />
								</slider-item>
							</slider>

						</div>
						<!-- /preview -->

						<!-- default -->
						<div
							v-else
							class="widgets-placeholder"
							:class="{ 'preview': preview }"
						>
							<div
								v-if="item.widgets.length"
								class="widgets-placeholder-preview"
							>
								<ul class="widgets-placeholder-icons">
									<li
										v-for="icon in findWidgetsNames(item.widgets)"
										:key="icon"
									>
										<img :src="`/images/widgets/${icon}.svg`" />
									</li>
								</ul>
							</div>
							<div
								v-else
								class="widgets-placeholder-empty"
							>
								<i class="el-icon-warning-outline" />
								{{ translations.labels.emptySlot }}
							</div>

						</div>
						<!-- default -->

					</div>
					<!-- /widgets -->

				</div>
			</grid-item>
		</grid-layout>
	</div>
</template>

<script>
import VueGridLayout from 'vue-grid-layout'
import { Slider, SliderItem } from 'vue-easy-slider'
import { EventBus } from '@/services/event-bus'

export default {
    name: 'PresentationLayout',
	components: {
		GridLayout: VueGridLayout.GridLayout,
		GridItem: VueGridLayout.GridItem,
		Slider: Slider,
		SliderItem: SliderItem,
		Widget: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Widgets/Index')
	},
	props: {
		backgroundColor: String,
		preview: Boolean,
		settings: Object,
		slots: Array
	},
    data () {
        return {
			options: {
				autoSize: false,
				cssTransforms: true,
				columns: parseInt(this.settings.columns),
				draggable: true,
				margin: [0,0],
				maxRows: parseInt(this.settings.rows),
				mirrored: false,
				preserveAspectRatio: true,
				preventCollision: true,
				rowHeight: null,
				resizable: true,
				verticalCompact: false
			},
			slotsItems: []
        }
    },
	watch: {
		'preview' () {
			this.calculateRowHeight()
			this.$refs.layout.style.backgroundColor = this.preview ? this.backgroundColor : 'transparent'
		},
		'slots': {
			handler () {
				this.slotsItems = []
				this.slotsItems = this.slots.length ? this.slots : []
			},
			immediate: true
		}
	},
	mounted () {
		window.addEventListener('resize', this.calculateRowHeight)
		this.calculateRowHeight()
	},
	destroyed () {
		window.removeEventListener('resize', this.calculateRowHeight)
    },
	methods: {
		calculateRowHeight () {
			if (this.$refs.layout) {
				this.options.rowHeight = this.$refs.layout.clientHeight / this.settings.rows
			}
		},
		deleteSlot (id) {
			this.slots.splice(this.slots.findIndex(item => {
				return item.i === id
			}), 1)
		},
		openWidgetManager (id) {
			EventBus.$emit('open-widget', id)
		},
		updateStatic (id) {
			EventBus.$emit('update-static', id)
		},
		findWidgetsNames (widgets) {
			let temp = []
			widgets.forEach(item => {
				temp.push(item.icon)
			})
			return temp
		}
	}
}
</script>

<style lang="scss" scoped>

.vue-grid {

	&-placeholder {
		background-color: rgba(0,0,0,.4) !important;
		border-radius: 0 !important;
	}

	&-item {

        transition: none !important;

		& > .vue-resizable-handle {
			bottom: 2px;
			right: 2px;
			z-index: 2 !important;
		}

	}

}

.layout {

	height: 100%;
	@include position($position: absolute, $top: 0, $left: 0);
	width: 100%;

	&-slot {

		height: 100%;
		position: relative;

		&-button {

			position: absolute;
			user-select: none;
			z-index: 2;

			&.static {
				left: 10px;
				top: 10px;
			}

			&.delete {
				left: 42px;
				top: 10px;
			}

			&.manage {
				right: 10px;
				top: 10px;
			}

			&.remove {
				bottom: 10px;
				right: 10px;
			}

		}

	}

}

.widgets {

	height: 100%;
	width: 100%;

	&-placeholder {

		background-color: rgba(255,255,255,.9);
		border: 1px solid darken($color-line, 50%);
		@include flex(flex, column, wrap, center, center);
		height: 100%;
		width: 100%;

		&:hover {
			background-color: rgba(255,255,255,1);
		}

		&-empty {

			color: lighten($color-text, 25%);

			[class^="el-icon-"] {
				font-size: 18px;
				vertical-align: text-top;
			}

		}

		&-icons {

			@include list-reset;
			@include flex(flex, row, wrap, center, center);

			li {

				margin: 0 5px;

				img {
					display: block;
					height: 30px;
				}

			}

		}

	}

	&-area {

		height: 100%;
		width: 100%;

		.slider {
			height: 100% !important;
			width: 100% !important;
		}

	}

}

</style>
